import React, { useEffect, useState, useCallback } from "react";
import {
  Form,
  Button,
  Spinner,
  Table,
  Card,
  Row,
  Col,
  Badge,
  Image,
} from "react-bootstrap";

const API_URL = "https://radheyelectrical.com/api/gallary.php";

export default function AdminGallary() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id: null,
    title: "",
    description: "",
    image: null,
  });

  // Memoized fetch to prevent unnecessary re-renders
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error("Failed to fetch data");
      const json = await res.json();
      setData(json.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
      alert("Error loading gallery data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    setForm((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  const resetForm = () => {
    setForm({ id: null, title: "", description: "", image: null });
    // Reset file input manually if needed
    const fileInput = document.getElementById("imageInput");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description);
    if (form.image) formData.append("image", form.image);
    if (form.id) formData.append("id", form.id);

    try {
      const res = await fetch(API_URL, {
        method: "POST", // Note: Many PHP scripts use POST + ID to simulate PUT
        body: formData,
      });

      if (!res.ok) throw new Error("Save failed");

      resetForm();
      fetchData();
    } catch (err) {
      console.error("Submit error:", err);
      alert("Error saving entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const res = await fetch(API_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      if (!res.ok) throw new Error("Delete failed");
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Could not delete item.");
    }
  };

  return (
    <div className="container py-5">
      <header className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="fw-bold text-primary">Gallery Management</h2>
          <p className="text-muted">Upload and manage your portfolio assets</p>
        </div>
        <Badge bg="info" className="px-3 py-2 text-uppercase">
          Admin Portal
        </Badge>
      </header>

      <Row>
        {/* FORM SECTION */}
        <Col lg={4}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <Card.Title className="mb-4 fw-bold">
                {form.id ? "Edit Entry" : "Add New Image"}
              </Card.Title>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label small className="fw-semibold">
                    Title
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Enter image title"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label small className="fw-semibold">
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    placeholder="Short description..."
                    value={form.description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label small className="fw-semibold">
                    Upload Image
                  </Form.Label>
                  <Form.Control
                    type="file"
                    id="imageInput"
                    onChange={handleFile}
                    accept="image/*"
                  />
                  {form.id && (
                    <Form.Text className="text-muted">
                      Leave empty to keep current image
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? (
                      <Spinner size="sm" animation="border" className="me-2" />
                    ) : null}
                    {form.id ? "Save Changes" : "Upload to Gallery"}
                  </Button>
                  {form.id && (
                    <Button variant="outline-secondary" onClick={resetForm}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* DATA SECTION */}
        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="grow" variant="primary" />
                </div>
              ) : (
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">Preview</th>
                      <th>Content</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td className="ps-4" style={{ width: "120px" }}>
                            <Image
                              src={`https://radheyelectrical.com/api/${item.image_path}`}
                              rounded
                              style={{
                                width: "80px",
                                height: "60px",
                                objectFit: "cover",
                              }}
                              alt={item.title}
                              fallback="https://via.placeholder.com/80x60?text=No+Img"
                            />
                          </td>
                          <td>
                            <div className="fw-bold">{item.title}</div>
                            <small
                              className="text-muted d-block text-truncate"
                              style={{ maxWidth: "250px" }}
                            >
                              {item.description}
                            </small>
                          </td>
                          <td className="text-end pe-4">
                            <Button
                              variant="light"
                              size="sm"
                              className="me-2 text-primary"
                              onClick={() => setForm({ ...item, image: null })}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              className="text-danger"
                              onClick={() => handleDelete(item.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-5 text-muted">
                          No gallery items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
